/* Copyright (c) 2016-2023 Planticle Pty Ltd */
"use strict";

import ko from "knockout";

import "./button-group.js";

// This turned out to be way harder than I thought.
// I'm not entirely satisfied with the solution but can't come up with any more elegant at the moment.
var CoordinateEntryViewModel = function(params) {
	var self = this;

	self.format = params.format || ko.observable('Decimal');
	self.showFormatSelector = params.showFormatSelector === undefined ? true : params.showFormatSelector;
	self.showFieldLabels = !!ko.unwrap(params.showFieldLabels);

	// Memory management
	self.disposables = [];
	self.dispose = function() {
		self.disposables.forEach(function(d) { d.dispose(); });
	};

	if(!self.format()) {
		self.format('Decimal');
	}

	self.point = params.point || ko.observable();

	function decimalString(x) {
		return x.toFixed(6);
	}

	function coordinate(type /* lat, lon */) {
		var dmax = (type == 'lat') ? 90 : 180;

		var self = {
			name: { lat: 'Latitude', lon: 'Longitude' }[type],
			decimal: ko.observable(),
			d: ko.observable(),
			m: ko.observable(),
			s: ko.observable(),
			direction: ko.observable({ lat: 'S', lon: 'E'}[type])
		};

		self.directionSign = ko.pureComputed(function() {
			switch(self.direction()) {
				case "N":
				case "E":
					return 1;
				case "S":
				case "W":
					return -1;
				default:
					return undefined;
			}
		});

		self.dmsValue = ko.pureComputed(function() {
			return {
				d: parseInt(self.d()),
				m: parseInt(self.m()),
				s: parseInt(self.s()),
				sign: self.directionSign()
			};
		});

		self.decimalValue = ko.pureComputed(function() {
			return self.directionSign() * parseFloat(self.decimal());
		});

		self.d.valid = ko.pureComputed(function() {
			return (+self.d() || 0) < dmax;
		});

		self.m.valid = ko.pureComputed(function() {
			return (+self.m() || 0) < 60;
		});

		self.s.valid = ko.pureComputed(function() {
			return (+self.s() || 0) < 60;
		});

		self.decimal.valid = ko.pureComputed(function() {
			return +self.decimal() < dmax;
		});

		self.dmsErrors = ko.pureComputed(function() {
			var dms = self.dmsValue();

			if(isNaN(dms.d) || isNaN(dms.m) || isNaN(dms.s) || isNaN(dms.sign)) {
				return true;
			}

			if(dms.d > dmax) {
				return self.name + " degrees must not be greater than " + dmax;
			}
			if(dms.m >= 60) {
				return self.name + " minutes must be less than 60";
			}
			if(dms.s >= 60) {
				return self.name + " seconds must be less than 60";
			}
		});

		self.decimalErrors = ko.pureComputed(function() {
			if(!self.decimal() || isNaN(self.decimalValue())) {
				return true;
			}

			if(+self.decimal() > dmax) {
				return self.name + " degrees must not be greater than " + dmax;
			}
		});

		return self;
	}

	function fromDMS(dms) {
		return (dms.d + (dms.m / 60) + (dms.s / 3600)) * dms.sign;
	}

	function toDMS(x) {
		var seconds = Math.round(Math.abs(x) * 3600);
		return {
			d: Math.floor(seconds / 3600),
			m: Math.floor(seconds / 60) % 60,
			s: seconds % 60,
			sign: Math.sign(x)
		};
	}

	self.lat = coordinate('lat');
	self.lon = coordinate('lon');

	// Update flows:
	// point -> decimal
	// decimal -> point
	// point -> dms
	// dms -> point
	function updateFromPoint(p) {
		if(p) {
			// point -> decimal
			if(self.lat.decimalValue() != p.lat || self.lon.decimalValue() != p.lon) {
				self.lat.decimal(decimalString(Math.abs(p.lat)));
				self.lon.decimal(decimalString(Math.abs(p.lon)));
			}

			self.lat.direction(p.lat >= 0 ? 'N' : 'S');
			self.lon.direction(p.lon >= 0 ? 'E' : 'W');

			// point -> dms
			var latDMS = toDMS(p.lat);
			var lonDMS = toDMS(p.lon);

			self.lat.d("" + latDMS.d);
			self.lat.m("" + latDMS.m);
			self.lat.s("" + latDMS.s);

			self.lon.d("" + lonDMS.d);
			self.lon.m("" + lonDMS.m);
			self.lon.s("" + lonDMS.s);
		} else {
			[self.lat, self.lon].forEach(function(x) {
				[x.decimal, x.d, x.m, x.s].forEach(function(y) {
					y("");
				});
			});
		}
	};

	self.disposables.push(self.point.subscribe(updateFromPoint));
	updateFromPoint(self.point());

	self.disposables.push(ko.computed(function() {
		// dms -> point
		if(self.format() == 'DMS') {
			if(!self.lat.dmsErrors() && !self.lon.dmsErrors()) {
				// We have a valid dms - we should update point, but ONLY if point does not already map to this DMS values
				// This is to avoid the scenario where the point is updated, then the DMS is updated, then the point is again
				// updated based on the DMS which is only accurate to the nearest second (~30m at equator)
				var p = self.point.peek(); // We don't want to create a dependency on point
				var newp = {
					lat: fromDMS(self.lat.dmsValue()),
					lon: fromDMS(self.lon.dmsValue())
				};

				if(!p || newp.lat != fromDMS(toDMS(p.lat)) || newp.lon != fromDMS(toDMS(p.lon))) {
					self.point(newp);
				}
			} else {
				var allBlank = [self.lat, self.lon].every(function(x) {
					return [x.d, x.m, x.s].every(function(y) { return y() == "" });
				});
				if(allBlank && self.point.peek()) {
					self.point(undefined);
				}
			}
		}
	}));

	function pointString(p) {
		return p ? ("[" + p.lat + ", " + p.lon + "]") : "null";
	}

	function dmsPairString() {
		return "[" + dmsString(self.lat.dmsValue()) + ", " + dmsString(self.lon.dmsValue()) + "]";
	}

	function dmsString(dms) {
		return "(" + [dms.d, dms.m, dms.s, dms.sign].join(", ") + ")";
	}

	self.disposables.push(ko.computed(function() {
		if(self.format() == 'Decimal') {
			if(!self.lat.decimalErrors() && !self.lon.decimalErrors()) {

				var p = self.point.peek();
				var newp = {
					lat: +self.lat.decimalValue(),
					lon: +self.lon.decimalValue()
				};
				// In theory this should never happen, but it does, and I think the problem is that I don't understand the evaluation
				// order. I'm finding it really hard to express composite bi-directional bindings in knockout. This whole component
				// is decidedly un-elegant for what should, I think, be simple. Probably needs a complete redesign. :-(
				if(isNaN(newp.lat) || isNaN(newp.lon)) {
					return;
				}

				self.point(newp);
			} else if(self.lat.decimal() == "" && self.lon.decimal() == "" && self.point.peek()) {
				self.point(undefined);
			}
		}
	}));

	function isValid() {
		if(self.format() == "Decimal") {
			return !(self.lat.decimalErrors() || self.lon.decimalErrors());
		} else if(self.format() == "DMS") {
			return !(self.lat.dmsErrors() || self.lon.dmsErrors());
		} else {
			return false;
		}
	}

	if(params.valid) {
		self.disposables.push(ko.computed(function() {
			params.valid(isValid());
		}));
	}

	function focusNextDMS(el) {
		var elements = [].slice.call(document.getElementsByClassName('dms'));
		var index = elements.indexOf(el);
		if(index >= 0 && index < elements.length - 1) {
			elements[index + 1].focus();
		}
	}

	// Automatically focus next field where possible
	self.handleKeyup = function(data, evt) {
		var el = evt.target;
		if(el.classList.contains('dms') && evt.key >= "0" && event.key <= "9") {
			// Longitude degrees is a special case because it can be 2 or 3 digits but not more than 180
			if(el.classList.contains('dms-d')) {
				if(el.parentElement.classList.contains('lon') && +el.value > 18 && +el.value <= 180) {
					focusNextDMS(el);
				}
				if(el.parentElement.classList.contains('lat') && el.value.length == 2 && +el.value <= 90) {
					focusNextDMS(el);
				}
			} else if(el.value.length == 2 && +el.value <= 60) {
				focusNextDMS(el);
			}
		}
	}
};

ko.components.register('coordinate-entry', {
	viewModel: function(params) {
		return new CoordinateEntryViewModel(params);
	},
	template: require('../../../templates/shared/coordinate-entry.html')
});

export default CoordinateEntryViewModel;
